import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { ToastContainer, useToast } from '../../components/ui/Toast';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import { createPaymentIntent, confirmPayment } from '../../services/paymentService';
import { useAuth } from '../../contexts/AuthContext';
import { sendDepositConfirmationEmail, sendPaymentFailureEmail } from '../../services/emailService';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

// ─── Inner Stripe Payment Form ────────────────────────────────────────────────
const StripePaymentForm = ({ clientSecret, paymentIntentId, onSuccess, onError, depositAmount, totalAmount, vendorName }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/payment-confirmation`
        },
        redirect: 'if_required'
      });

      if (error) {
        onError(error?.message ?? 'Payment failed. Please try again.');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        await confirmPayment(paymentIntent?.id);
        onSuccess(paymentIntent);
      } else {
        onError('Payment was not completed. Please try again.');
      }
    } catch (err) {
      onError(err?.message ?? 'An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} id="stripe-payment-form">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-4">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
            <Icon name="CreditCard" size={14} className="text-white" />
          </div>
          Secure Payment
        </h3>

        {/* Milestone Payment Breakdown */}
        <div className="mb-4 rounded-xl overflow-hidden border border-gray-100">
          <div className="p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
            Milestone Payment Schedule
          </div>
          <div className="divide-y divide-gray-100">
            <div className="flex items-center justify-between px-4 py-3" style={{ background: 'linear-gradient(to right, rgba(245,138,84,0.08), rgba(240,111,107,0.08))' }}>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>1</div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Deposit — Due Now</p>
                  <p className="text-xs text-gray-500">30% to kick off the service</p>
                </div>
              </div>
              <span className="text-base font-bold" style={{ color: '#F06F6B' }}>${depositAmount?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-300 text-white text-xs font-bold">2</div>
                <div>
                  <p className="text-sm font-semibold text-gray-500">Completion — Due on Finish</p>
                  <p className="text-xs text-gray-400">70% upon service completion</p>
                </div>
              </div>
              <span className="text-base font-bold text-gray-400">${((totalAmount || 0) - (depositAmount || 0))?.toLocaleString()}</span>
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-t border-gray-100">
            <span className="text-sm font-medium text-gray-500">Total Project Cost</span>
            <span className="text-sm font-bold text-gray-700">${totalAmount?.toLocaleString()}</span>
          </div>
        </div>

        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card', 'apple_pay', 'google_pay']
          }} />
        
      </div>
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 shadow-md"
        style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
        
        {isProcessing ?
        <>
            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Processing Payment...
          </> :

        <>
            <Icon name="Lock" size={16} />
            Pay ${depositAmount?.toLocaleString()} Deposit & Launch Stack
            <Icon name="Rocket" size={16} />
          </>
        }
      </button>
      <div className="flex items-center justify-center gap-6 mt-4">
        {[
        { icon: 'Shield', label: 'SSL Protected' },
        { icon: 'Lock', label: 'PCI Compliant' },
        { icon: 'CheckCircle2', label: 'Stripe Secured' }]?.map((badge, i) =>
        <div key={i} className="flex items-center gap-1.5">
            <Icon name={badge?.icon} size={14} className="text-emerald-600" />
            <span className="text-xs font-medium text-gray-600">{badge?.label}</span>
          </div>
        )}
      </div>
    </form>);

};

// ─── Main Checkout Component ──────────────────────────────────────────────────
const CheckoutProcess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorName = searchParams?.get('vendor') || 'Amazon';
  const projectId = searchParams?.get('project') || '1';
  const { toasts, addToast, removeToast } = useToast();
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  const [formData, setFormData] = useState({
    projectSpecifications: {
      deliveryDate: '',
      specialRequirements: '',
      installationNeeded: false,
      warrantyExtension: false
    },
    billingInfo: {
      firstName: '',
      lastName: '',
      email: user?.email || '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: ''
    },
    communicationPreferences: {
      emailUpdates: true,
      smsNotifications: false,
      weeklyReports: true,
      milestoneAlerts: true
    },
    agreedToTerms: false,
    agreedToPrivacy: false
  });

  const projectData = {
    id: projectId,
    name: "Service Stack",
    category: "Services",
    timeline: "As agreed with vendor"
  };

  const getVendorData = (name) => {
    const vendors = {
      'Amazon': {
        name: 'Amazon',
        logo: "https://img.rocket.new/generatedImages/rocket_gen_img_108d1b345-1772185086893.png",
        logoAlt: 'Apple MacBook Pro 14-inch with M3 Pro chip in Space Gray',
        rating: 4.6,
        reviewCount: 1247,
        quote: { price: 1999, originalPrice: 2199, product: 'Apple MacBook Pro 14-inch', specifications: ['M3 Pro Chip with 11-core CPU', '18GB Unified Memory', '512GB SSD Storage'], deliveryTime: '1-2 days', terms: 'Prime shipping, 30-day returns', warranty: '1-year limited warranty' },
        contact: { email: 'support@amazon.com', phone: '(888) 280-4331' }
      },
      'Best Buy': {
        name: 'Best Buy',
        logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11ad868de-1766607648821.png",
        logoAlt: 'Apple MacBook Air 13-inch in Midnight color with M3 chip',
        rating: 4.4,
        reviewCount: 892,
        quote: { price: 1399, originalPrice: 1499, product: 'Apple MacBook Air 13-inch', specifications: ['M3 Chip with 8-core CPU', '16GB Unified Memory', '512GB SSD Storage'], deliveryTime: 'Same day pickup available', terms: 'Geek Squad support, price match guarantee', warranty: 'Built for Apple Intelligence' },
        contact: { email: 'support@bestbuy.com', phone: '(888) 237-8289' }
      },
      'B&H Photo': {
        name: 'B&H Photo',
        logo: "https://img.rocket.new/generatedImages/rocket_gen_img_179123d6d-1772091920622.png",
        logoAlt: 'Apple MacBook Pro 14-inch with latest M4 chip',
        rating: 4.8,
        reviewCount: 2156,
        quote: { price: 2299, originalPrice: 2499, product: 'Apple MacBook Pro 14-inch', specifications: ['M4 Chip with 10-core CPU', '16GB Unified Memory', '512GB SSD Storage'], deliveryTime: '2-3 days', terms: 'Professional support, extended warranty options', warranty: 'Latest M4 technology' },
        contact: { email: 'info@bhphotovideo.com', phone: '(800) 606-6969' }
      }
    };
    return vendors?.[name] || vendors?.['Amazon'];
  };

  const selectedVendor = getVendorData(vendorName);
  const totalAmount = selectedVendor?.quote?.price || 0;
  const depositAmount = Math.round(totalAmount * 0.30); // 30% deposit
  const completionAmount = totalAmount - depositAmount; // 70% on completion

  const steps = [
  { id: 1, title: 'Vendor Confirmation', icon: 'CheckCircle2', completed: currentStep > 1 },
  { id: 2, title: 'Billing Details', icon: 'User', completed: currentStep > 2 },
  { id: 3, title: 'Payment', icon: 'CreditCard', completed: false }];


  const handleInputChange = (section, field, value) => {
    if (!section) {
      setFormData((prev) => ({ ...prev, [field]: value }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [section]: { ...prev?.[section], [field]: value }
      }));
    }
  };

  // Create payment intent when reaching step 3 — always 30% deposit
  const handleCreatePaymentIntent = useCallback(async () => {
    if (clientSecret) return;
    setIsCreatingIntent(true);
    setPaymentError(null);
    try {
      const result = await createPaymentIntent({
        paymentData: {
          amount: depositAmount,
          currency: 'usd',
          milestoneType: 'deposit',
          totalProjectCost: totalAmount,
          description: `30% deposit for ${selectedVendor?.name} — ${selectedVendor?.quote?.product}`,
          projectId: projectId,
          vendorName: selectedVendor?.name,
          metadata: {
            productName: selectedVendor?.quote?.product,
            totalProjectCost: String(totalAmount)
          }
        },
        customerInfo: {
          userId: user?.id ?? null,
          firstName: formData?.billingInfo?.firstName,
          lastName: formData?.billingInfo?.lastName,
          email: formData?.billingInfo?.email || user?.email,
          stripeCustomerId: stripeCustomerId,
          billing: {
            address_line_1: formData?.billingInfo?.address,
            city: formData?.billingInfo?.city,
            state: formData?.billingInfo?.state,
            postal_code: formData?.billingInfo?.zipCode,
            country: 'US'
          }
        }
      });
      setClientSecret(result?.clientSecret);
      setPaymentIntentId(result?.paymentIntentId);
      setStripeCustomerId(result?.stripeCustomerId);
    } catch (err) {
      setPaymentError(err?.message ?? 'Failed to initialize payment. Please try again.');
      addToast('Failed to initialize payment. Please try again.', 'error');
    } finally {
      setIsCreatingIntent(false);
    }
  }, [clientSecret, depositAmount, totalAmount, selectedVendor, projectId, user, formData, stripeCustomerId]);

  const handleNext = () => {
    if (currentStep < 3) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setCurrentStep((prev) => prev + 1);
        const messages = ['Vendor details confirmed!', 'Billing details saved!'];
        addToast(messages?.[currentStep - 1] || 'Step completed!', 'success');
      }, 600);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      navigate(`/project-comparison?project=${projectId}`);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    addToast('Deposit paid! Your Stack is now live 🚀', 'success', 4000);
    // Send deposit confirmation email to the user
    const email = formData?.billingInfo?.email || user?.email;
    if (email) {
      const totalAmt = selectedVendor?.quote?.price || 0;
      const depAmt = Math.round(totalAmt * 0.30);
      sendDepositConfirmationEmail(email, {
        recipientName: formData?.billingInfo?.firstName || user?.user_metadata?.full_name || '',
        projectName: selectedVendor?.quote?.product || 'Service Stack',
        vendorName: selectedVendor?.name || vendorName,
        depositAmount: depAmt,
        totalAmount: totalAmt,
        completionAmount: totalAmt - depAmt,
        projectId,
        isVendor: false,
      })?.catch(() => {});
    }
    setTimeout(() => {
      navigate(`/payment-confirmation?paymentIntentId=${paymentIntent?.id}&project=${projectId}&vendor=${vendorName}`);
    }, 1000);
  };

  const handlePaymentError = (message) => {
    setPaymentError(message);
    addToast(message, 'error');
    // Send payment failure email to the user
    const email = formData?.billingInfo?.email || user?.email;
    if (email) {
      sendPaymentFailureEmail(email, {
        recipientName: formData?.billingInfo?.firstName || user?.user_metadata?.full_name || '',
        projectName: selectedVendor?.quote?.product || 'Service Stack',
        vendorName: selectedVendor?.name || vendorName,
        amount: Math.round((selectedVendor?.quote?.price || 0) * 0.30),
        milestoneType: 'deposit',
        errorMessage: message,
        projectId,
        isVendor: false,
      })?.catch(() => {});
    }
  };

  // Trigger payment intent creation when step 3 is reached
  useEffect(() => {
    if (currentStep === 3 && !clientSecret) {
      handleCreatePaymentIntent();
    }
  }, [currentStep]);

  const isStepValid = () => {
    switch (currentStep) {
      case 1:return formData?.projectSpecifications?.deliveryDate;
      case 2:return formData?.billingInfo?.firstName && formData?.billingInfo?.email && formData?.agreedToTerms && formData?.agreedToPrivacy;
      case 3:return false; // Payment handled by Stripe form submit
      default:return false;
    }
  };

  const stripeOptions = clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#F06F6B',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '12px'
      }
    }
  } : null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Vendor Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
                  <Icon name="Store" size={14} className="text-white" />
                </div>
                Selected Vendor
              </h3>
              <div className="flex items-start gap-4">
                <img
                  src={selectedVendor?.logo}
                  alt={selectedVendor?.logoAlt}
                  className="w-16 h-16 rounded-2xl object-cover border border-gray-100 shadow-sm" />
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className="text-xl font-bold text-gray-900">{selectedVendor?.name}</h4>
                    <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-lg">
                      <Icon name="Star" size={13} className="text-amber-500" />
                      <span className="text-xs font-semibold text-amber-700">{selectedVendor?.rating} ({selectedVendor?.reviewCount?.toLocaleString()})</span>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm mb-3">{selectedVendor?.quote?.product}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-500 mb-1">Price</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-emerald-600">${selectedVendor?.quote?.price?.toLocaleString()}</span>
                        {selectedVendor?.quote?.originalPrice &&
                        <span className="text-sm text-gray-400 line-through">${selectedVendor?.quote?.originalPrice?.toLocaleString()}</span>
                        }
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-500 mb-1">Delivery</p>
                      <p className="text-sm font-semibold text-gray-900">{selectedVendor?.quote?.deliveryTime}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Milestone Payment Schedule */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F06F6B, #E0528A)' }}>
                  <Icon name="Milestone" size={14} className="text-white" />
                </div>
                Milestone Payment Schedule
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'linear-gradient(to right, rgba(245,138,84,0.08), rgba(240,111,107,0.08))', border: '1px solid rgba(245,138,84,0.2)' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>1</div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">Deposit — Due Now</p>
                    <p className="text-xs text-gray-500">Charged today to kick off the service</p>
                  </div>
                  <span className="text-base font-bold" style={{ color: '#F06F6B' }}>${depositAmount?.toLocaleString()} <span className="text-xs font-normal text-gray-400">(30%)</span></span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-300 text-white text-sm font-bold flex-shrink-0">2</div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-500">Completion — Due on Finish</p>
                    <p className="text-xs text-gray-400">Charged when service is marked complete</p>
                  </div>
                  <span className="text-base font-bold text-gray-400">${completionAmount?.toLocaleString()} <span className="text-xs font-normal">(70%)</span></span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Total Project Cost</span>
                  <span className="text-base font-bold text-gray-800">${totalAmount?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Quote Summary */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F06F6B, #E0528A)' }}>
                  <Icon name="FileText" size={14} className="text-white" />
                </div>
                Quote Summary
              </h3>
              <div className="space-y-2.5">
                {selectedVendor?.quote?.specifications?.map((spec, index) =>
                <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" size={11} className="text-emerald-600" />
                    </div>
                    <span className="text-sm text-gray-700">{spec}</span>
                  </div>
                )}
              </div>
              <div className="border-t border-gray-100 mt-4 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Terms:</span>
                  <span className="text-sm text-gray-700">{selectedVendor?.quote?.terms}</span>
                </div>
              </div>
            </div>

            {/* Stack Specifications */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)' }}>
                  <Icon name="Settings" size={14} className="text-white" />
                </div>
                Stack Specifications
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Delivery Date</label>
                  <Input
                    type="date"
                    value={formData?.projectSpecifications?.deliveryDate}
                    onChange={(e) => handleInputChange('projectSpecifications', 'deliveryDate', e?.target?.value)}
                    className="w-full" />
                  
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements</label>
                  <Input
                    type="text"
                    placeholder="Any special setup or configuration needs"
                    value={formData?.projectSpecifications?.specialRequirements}
                    onChange={(e) => handleInputChange('projectSpecifications', 'specialRequirements', e?.target?.value)}
                    className="w-full" />
                  
                </div>
              </div>
              <div className="space-y-3">
                <Checkbox
                  checked={formData?.projectSpecifications?.installationNeeded}
                  onChange={(checked) => handleInputChange('projectSpecifications', 'installationNeeded', checked)}
                  label="Installation/Setup Service Required" />
                
                <Checkbox
                  checked={formData?.projectSpecifications?.warrantyExtension}
                  onChange={(checked) => handleInputChange('projectSpecifications', 'warrantyExtension', checked)}
                  label="Extended Warranty Coverage" />
                
              </div>
            </div>
          </div>);


      case 2:
        return (
          <div className="space-y-6">
            {/* Deposit Info */}
            <div
              className="rounded-2xl p-6"
              style={{ background: 'linear-gradient(to right, rgba(245,138,84,0.08), rgba(224,82,138,0.08))', border: '1px solid rgba(245,138,84,0.2)' }}>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
                  <Icon name="Info" size={16} className="text-white" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Deposit Required</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                { label: 'Total Cost', value: `$${totalAmount?.toLocaleString()}` },
                { label: 'Deposit (30%)', value: `$${depositAmount?.toLocaleString()}` },
                { label: 'Remaining', value: `$${((totalAmount || 0) - depositAmount)?.toLocaleString()}` }]?.map((item, i) =>
                <div key={i} className="bg-white rounded-xl p-3 text-center shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">{item?.label}</p>
                    <p className="text-lg font-bold" style={{ color: '#F06F6B' }}>{item?.value}</p>
                  </div>
                )}
              </div>
            </div>
            {/* Billing Info */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F06F6B, #E0528A)' }}>
                  <Icon name="User" size={14} className="text-white" />
                </div>
                Billing Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input type="text" placeholder="First Name" value={formData?.billingInfo?.firstName} onChange={(e) => handleInputChange('billingInfo', 'firstName', e?.target?.value)} />
                <Input type="text" placeholder="Last Name" value={formData?.billingInfo?.lastName} onChange={(e) => handleInputChange('billingInfo', 'lastName', e?.target?.value)} />
                <Input type="email" placeholder="Email Address" value={formData?.billingInfo?.email} onChange={(e) => handleInputChange('billingInfo', 'email', e?.target?.value)} />
                <Input type="tel" placeholder="Phone Number" value={formData?.billingInfo?.phone} onChange={(e) => handleInputChange('billingInfo', 'phone', e?.target?.value)} />
                <div className="md:col-span-2">
                  <Input type="text" placeholder="Billing Address" value={formData?.billingInfo?.address} onChange={(e) => handleInputChange('billingInfo', 'address', e?.target?.value)} />
                </div>
                <Input type="text" placeholder="City" value={formData?.billingInfo?.city} onChange={(e) => handleInputChange('billingInfo', 'city', e?.target?.value)} />
                <Input type="text" placeholder="State" value={formData?.billingInfo?.state} onChange={(e) => handleInputChange('billingInfo', 'state', e?.target?.value)} />
              </div>
            </div>
            {/* Communication Preferences */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)' }}>
                  <Icon name="Bell" size={14} className="text-white" />
                </div>
                Communication Preferences
              </h3>
              <div className="space-y-3">
                <Checkbox checked={formData?.communicationPreferences?.emailUpdates} onChange={(checked) => handleInputChange('communicationPreferences', 'emailUpdates', checked)} label="Email updates on stack progress" />
                <Checkbox checked={formData?.communicationPreferences?.smsNotifications} onChange={(checked) => handleInputChange('communicationPreferences', 'smsNotifications', checked)} label="SMS notifications for important milestones" />
                <Checkbox checked={formData?.communicationPreferences?.weeklyReports} onChange={(checked) => handleInputChange('communicationPreferences', 'weeklyReports', checked)} label="Weekly progress reports" />
                <Checkbox checked={formData?.communicationPreferences?.milestoneAlerts} onChange={(checked) => handleInputChange('communicationPreferences', 'milestoneAlerts', checked)} label="Milestone completion alerts" />
              </div>
            </div>
            {/* Terms */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F06F6B, #E0528A)' }}>
                  <Icon name="FileCheck" size={14} className="text-white" />
                </div>
                Terms & Conditions
              </h3>
              <div className="space-y-4">
                <Checkbox
                  checked={formData?.agreedToTerms}
                  onChange={(checked) => handleInputChange('', 'agreedToTerms', checked)}
                  label={<span>I agree to the <a href="#" className="hover:underline font-medium" style={{ color: '#F06F6B' }}>Terms of Service</a> and <a href="#" className="hover:underline font-medium" style={{ color: '#F06F6B' }}>Vendor Agreement</a></span>} />
                
                <Checkbox
                  checked={formData?.agreedToPrivacy}
                  onChange={(checked) => handleInputChange('', 'agreedToPrivacy', checked)}
                  label={<span>I acknowledge the <a href="#" className="hover:underline font-medium" style={{ color: '#F06F6B' }}>Privacy Policy</a> and consent to data processing</span>} />
                
              </div>
            </div>
            {/* Dispute Resolution */}
            <div className="rounded-2xl p-5" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800 mb-1 text-sm">Dispute Resolution</h4>
                  <p className="text-sm text-amber-700">In case of any issues, Stackd provides mediation services and payment protection. Contact our support team at support@stackd.com.</p>
                </div>
              </div>
            </div>
          </div>);


      case 3:
        return (
          <div className="space-y-4">
            {isCreatingIntent ?
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
                  <svg className="animate-spin h-7 w-7 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
                <p className="text-gray-600 font-medium">Preparing secure payment...</p>
              </div> :
            paymentError && !clientSecret ?
            <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-6">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-red-800 mb-1">Payment Setup Failed</h4>
                    <p className="text-sm text-red-600">{paymentError}</p>
                    <button
                    onClick={handleCreatePaymentIntent}
                    className="mt-3 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
                    
                      Try Again
                    </button>
                  </div>
                </div>
              </div> :
            clientSecret && stripeOptions ?
            <>
                {paymentError &&
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{paymentError}</p>
                  </div>
              }
                <Elements stripe={stripePromise} options={stripeOptions}>
                  <StripePaymentForm
                  clientSecret={clientSecret}
                  paymentIntentId={paymentIntentId}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  depositAmount={depositAmount}
                  totalAmount={totalAmount}
                  vendorName={selectedVendor?.name} />
                
                </Elements>
              </> :
            null}
          </div>);


      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <main className="pt-32">
        {/* Gradient Hero Banner */}
        <div
          className="relative overflow-hidden py-10 px-4 sm:px-6 lg:px-8"
          style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}>
          
          {/* Decorative blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute bottom-0 -left-8 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
            {/* Floating icons */}
            {[
            { name: 'ShoppingCart', top: '20%', left: '2%', rotate: '-10deg', opacity: 0.35 },
            { name: 'CreditCard', top: '55%', left: '1%', rotate: '8deg', opacity: 0.3 },
            { name: 'Package', top: '20%', right: '2%', rotate: '12deg', opacity: 0.35 },
            { name: 'CheckCircle', top: '55%', right: '1%', rotate: '-9deg', opacity: 0.3 }]?.
            map((item, i) =>
            <div
              key={i}
              className="absolute"
              style={{ top: item?.top, left: item?.left, right: item?.right, opacity: item?.opacity, transform: `rotate(${item?.rotate})` }}>
              
                <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur-sm">
                  <Icon name={item?.name} size={22} color="white" />
                </div>
              </div>
            )}
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Badge */}
            <div className="flex mb-3">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                <span className="text-white/90 text-sm font-medium tracking-wide">Step 4 of 4 — Buy</span>
              </div>
            </div>

            <Breadcrumb
              projectName={projectData?.name}
              customPath={[
              { label: 'Stacks', path: '/all-projects', isClickable: true },
              { label: projectData?.name, path: null, isClickable: false },
              { label: 'Compare', path: `/project-comparison?project=${projectId}`, isClickable: true },
              { label: 'Checkout', path: null, isClickable: false }]
              } />
            
            <h1 className="text-3xl font-bold text-white mb-1">Checkout</h1>
            <p className="text-white/80">Finalize your vendor selection and stack details</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Step Progress */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-8">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) =>
              <React.Fragment key={step?.id}>
                  <div className="flex items-center gap-3">
                    <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    currentStep === step?.id ? 'text-white shadow-md' :
                    step?.completed ? 'text-white' : 'bg-gray-100 text-gray-400'}`
                    }
                    style={
                    currentStep === step?.id ?
                    { background: 'linear-gradient(135deg, #F58A54, #F06F6B)' } :
                    step?.completed ?
                    { background: 'linear-gradient(135deg, #10b981, #059669)' } :
                    {}
                    }>
                    
                      {step?.completed ?
                    <Icon name="Check" size={18} /> :
                    <Icon name={step?.icon} size={18} />
                    }
                    </div>
                    <div className="hidden sm:block">
                      <p className={`text-sm font-semibold ${
                    currentStep === step?.id ? 'text-gray-900' :
                    step?.completed ? 'text-emerald-600' : 'text-gray-400'}`
                    }>
                        {step?.title}
                      </p>
                    </div>
                  </div>
                  {index < steps?.length - 1 &&
                <div className={`flex-1 h-0.5 mx-3 ${
                step?.completed ? 'bg-emerald-400' : 'bg-gray-200'}`
                } />
                }
                </React.Fragment>
              )}
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation — hidden on step 3 (Stripe form has its own submit) */}
          {currentStep < 3 &&
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <button
              onClick={handleBack}
              disabled={isLoading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              
                <Icon name="ArrowLeft" size={16} />
                {currentStep === 1 ? 'Back to Comparison' : 'Previous Step'}
              </button>
              <button
              onClick={handleNext}
              disabled={!isStepValid() || isLoading}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 shadow-md"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
              
                {isLoading ?
              <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing...
                  </> :

              <>
                    Next Step
                    <Icon name="ArrowRight" size={16} />
                  </>
              }
              </button>
            </div>
          }
          {currentStep === 3 &&
          <div className="pt-6 border-t border-gray-200">
              <button
              onClick={handleBack}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors">
              
                <Icon name="ArrowLeft" size={16} />
                Back to Billing
              </button>
            </div>
          }
        </div>
      </main>
    </div>
  );

 };

export default CheckoutProcess;